import {
    DollarOutlined,
    FileExcelOutlined,
    GoldOutlined,
    NumberOutlined,
    PrinterOutlined,
    ProfileOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import {
    Button,
    Card,
    Col,
    DatePicker,
    List,
    message,
    Row,
    Space,
    Statistic,
    Table,
    Tabs,
    Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { parseContentDispositionFilename } from '../domain/downloads';
import useRequest from '../hooks/useRequest';

const { Title, Text } = Typography;

type RapoarteRow = Record<string, unknown>;

interface ActiveSummary {
    count: number;
    totalLei: number;
}

interface ConsignmentSummary {
    total: number;
    au585: number;
    au750: number;
}

interface ListResponse {
    success: boolean;
    data?: RapoarteRow[];
    summary?: ActiveSummary & ConsignmentSummary;
}

interface AnnexDate {
    date: string;
}

const formatDate = (value: unknown): string =>
    value ? dayjs(value as string).format('DD/MM/YYYY') : '';

const formatNumber = (value: unknown, decimals = 2): string => {
    const parsed = Number(value ?? 0);
    return Number.isFinite(parsed)
        ? parsed.toLocaleString(undefined, {
              minimumFractionDigits: decimals,
              maximumFractionDigits: decimals,
          })
        : '';
};

interface SummaryStatProps {
    title: string;
    value: number;
    accent: string;
    icon: React.ReactNode;
    precision?: number;
    suffix?: string;
}

const SummaryStat = ({ title, value, accent, icon, precision, suffix }: SummaryStatProps) => (
    <Card
        className="kpi-card"
        style={{ ['--kpi-accent' as string]: accent } as React.CSSProperties}
        styles={{ body: { padding: 16 } }}
    >
        <Space align="center" size={14}>
            <span className="kpi-badge">{icon}</span>
            <Statistic
                title={title}
                value={value}
                precision={precision}
                suffix={suffix}
                valueStyle={{ fontWeight: 700 }}
            />
        </Space>
    </Card>
);

const Rapoarte = () => {
    const { t, i18n } = useTranslation();
    const request = useRequest();
    const { token, API_BASE_URL } = useAuth();
    const { metals } = useApi();

    const [activeTab, setActiveTab] = useState('gold-contracts');
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<RapoarteRow[]>([]);
    const [consignmentSummary, setConsignmentSummary] = useState<ConsignmentSummary | null>(null);
    const [activeSummary, setActiveSummary] = useState<ActiveSummary | null>(null);
    const [referenceDate, setReferenceDate] = useState<dayjs.Dayjs>(dayjs());

    const [annexDates, setAnnexDates] = useState<string[]>([]);
    const [selectedAnnexDate, setSelectedAnnexDate] = useState<string | null>(null);
    const [annexKind, setAnnexKind] = useState<'gold' | 'electronics'>('gold');
    const [annexRows, setAnnexRows] = useState<RapoarteRow[]>([]);

    const usesDate = activeTab === 'consignment' || activeTab === 'active-accounting';
    const supportsExport = activeTab !== 'annexes';

    const loadTab = useCallback(async () => {
        if (activeTab === 'annexes') {
            return;
        }
        setLoading(true);
        setConsignmentSummary(null);
        setActiveSummary(null);
        try {
            const params =
                activeTab === 'consignment' || activeTab === 'active-accounting'
                    ? { date: referenceDate.format('YYYY-MM-DD') }
                    : undefined;
            const result = await request<ListResponse>({
                path: `/rapoarte/${activeTab}`,
                params,
            });
            if (result.success) {
                setRows(result.data ?? []);
                if (activeTab === 'consignment' && result.summary) {
                    setConsignmentSummary(result.summary);
                }
                if (activeTab === 'active-accounting' && result.summary) {
                    setActiveSummary(result.summary);
                }
            } else {
                setRows([]);
                message.error(t('rapoarte.loadError'));
            }
        } catch (error) {
            console.error('Rapoarte load error:', error);
            setRows([]);
            message.error(t('rapoarte.loadError'));
        } finally {
            setLoading(false);
        }
    }, [activeTab, referenceDate, request, t]);

    useEffect(() => {
        loadTab();
    }, [loadTab]);

    const loadAnnexDates = useCallback(async () => {
        try {
            const result = await request<{ success: boolean; data?: AnnexDate[] }>({
                path: '/rapoarte/annex-dates',
            });
            if (result.success) {
                setAnnexDates((result.data ?? []).map(item => item.date));
            }
        } catch (error) {
            console.error('Rapoarte annex dates error:', error);
        }
    }, [request]);

    useEffect(() => {
        if (activeTab === 'annexes') {
            loadAnnexDates();
        }
    }, [activeTab, loadAnnexDates]);

    const loadAnnex = useCallback(
        async (date: string, kind: 'gold' | 'electronics') => {
            setSelectedAnnexDate(date);
            setAnnexKind(kind);
            setLoading(true);
            try {
                const endpoint = kind === 'gold' ? 'annex-gold' : 'annex-electronics';
                const result = await request<ListResponse>({
                    path: `/rapoarte/${endpoint}`,
                    params: { date },
                });
                if (result.success) {
                    setAnnexRows(result.data ?? []);
                } else {
                    setAnnexRows([]);
                    message.error(t('rapoarte.loadError'));
                }
            } catch (error) {
                console.error('Rapoarte annex load error:', error);
                setAnnexRows([]);
                message.error(t('rapoarte.loadError'));
            } finally {
                setLoading(false);
            }
        },
        [request, t],
    );

    const handleExport = useCallback(async () => {
        try {
            const params = new URLSearchParams({ lang: i18n.language });
            if (usesDate) {
                params.set('date', referenceDate.format('YYYY-MM-DD'));
            }
            const response = await fetch(`${API_BASE_URL}/rapoarte/export/${activeTab}?${params.toString()}`, {
                headers: {
                    ...(token && { Authorization: `Bearer ${token}` }),
                    Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                },
            });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download =
                parseContentDispositionFilename(response.headers.get('Content-Disposition')) ??
                `${activeTab}_${referenceDate.format('YYYY-MM-DD')}.xlsx`;
            anchor.click();
            URL.revokeObjectURL(url);
            message.success(t('common.exportSuccess'));
        } catch (error) {
            console.error('Rapoarte export error:', error);
            message.error(t('rapoarte.exportError'));
        }
    }, [API_BASE_URL, activeTab, i18n.language, referenceDate, token, usesDate, t]);

    const handleExportAnnex = useCallback(async () => {
        if (!selectedAnnexDate) {
            return;
        }
        const result =
            annexKind === 'gold'
                ? await metals.exportGoldAnnex(selectedAnnexDate)
                : await metals.exportElAltAnnex(selectedAnnexDate);
        if (result.success && result.data) {
            const url = URL.createObjectURL(result.data);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `${annexKind === 'gold' ? 'anexa_aur' : 'el_alt'}_${selectedAnnexDate}.xlsx`;
            anchor.click();
            URL.revokeObjectURL(url);
            message.success(t('common.exportSuccess'));
        } else {
            message.error(result.error ?? t('rapoarte.exportError'));
        }
    }, [annexKind, metals, selectedAnnexDate, t]);

    const goldColumns: ColumnsType<RapoarteRow> = [
        { title: t('rapoarte.contractNumber'), dataIndex: 'contractNumber', key: 'contractNumber' },
        { title: t('rapoarte.clientName'), dataIndex: 'clientName', key: 'clientName', ellipsis: true },
        { title: t('rapoarte.metalType'), dataIndex: 'metalType', key: 'metalType' },
        { title: t('rapoarte.purity'), dataIndex: 'purity', key: 'purity', align: 'right' },
        {
            title: t('rapoarte.quantity'),
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'right',
            render: value => formatNumber(value),
        },
        {
            title: t('rapoarte.valueLei'),
            dataIndex: 'valueRon',
            key: 'valueRon',
            align: 'right',
            render: value => formatNumber(value, 0),
        },
    ];

    const electronicsColumns: ColumnsType<RapoarteRow> = [
        { title: t('rapoarte.contractNumber'), dataIndex: 'contractNumber', key: 'contractNumber' },
        { title: t('rapoarte.date'), dataIndex: 'date', key: 'date', render: formatDate },
        { title: t('rapoarte.clientName'), dataIndex: 'clientName', key: 'clientName', ellipsis: true },
        { title: t('rapoarte.description'), dataIndex: 'description', key: 'description', ellipsis: true },
        { title: t('rapoarte.type'), dataIndex: 'type', key: 'type' },
        {
            title: t('rapoarte.quantity'),
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'right',
            render: value => formatNumber(value),
        },
    ];

    const soldGoodsColumns: ColumnsType<RapoarteRow> = [
        { title: t('rapoarte.date'), dataIndex: 'date', key: 'date', render: formatDate },
        { title: t('rapoarte.invoiceNumber'), dataIndex: 'invoiceNumber', key: 'invoiceNumber' },
        { title: t('rapoarte.contractNumber'), dataIndex: 'contractNumber', key: 'contractNumber' },
        { title: t('rapoarte.clientName'), dataIndex: 'clientName', key: 'clientName', ellipsis: true },
        {
            title: t('rapoarte.amountEur'),
            dataIndex: 'amountEur',
            key: 'amountEur',
            align: 'right',
            render: value => formatNumber(value),
        },
        {
            title: t('rapoarte.exchangeRate'),
            dataIndex: 'exchangeRate',
            key: 'exchangeRate',
            align: 'right',
            render: value => formatNumber(value, 4),
        },
        {
            title: t('rapoarte.amountLei'),
            dataIndex: 'amountLei',
            key: 'amountLei',
            align: 'right',
            render: value => formatNumber(value, 0),
        },
    ];

    const consignmentColumns: ColumnsType<RapoarteRow> = [
        { title: t('rapoarte.code'), dataIndex: 'code', key: 'code' },
        { title: t('rapoarte.date'), dataIndex: 'date', key: 'date', render: formatDate },
        { title: t('rapoarte.description'), dataIndex: 'description', key: 'description', ellipsis: true },
        {
            title: t('rapoarte.quantity'),
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'right',
            render: value => formatNumber(value),
        },
        { title: t('rapoarte.title2'), dataIndex: 'title', key: 'title' },
    ];

    const activeAccountingColumns: ColumnsType<RapoarteRow> = [
        { title: t('rapoarte.contractNumber'), dataIndex: 'contractNumber', key: 'contractNumber' },
        { title: t('rapoarte.clientName'), dataIndex: 'clientName', key: 'clientName', ellipsis: true },
        { title: t('rapoarte.object'), dataIndex: 'itemDescription', key: 'itemDescription', ellipsis: true },
        { title: t('rapoarte.quantity'), dataIndex: 'quantity', key: 'quantity', align: 'right' },
        {
            title: t('rapoarte.amountLei'),
            dataIndex: 'amountLei',
            key: 'amountLei',
            align: 'right',
            render: value => formatNumber(value, 0),
        },
    ];

    const cashHandoverColumns: ColumnsType<RapoarteRow> = [
        { title: t('rapoarte.date'), dataIndex: 'date', key: 'date', render: formatDate },
        { title: t('rapoarte.time'), dataIndex: 'time', key: 'time' },
        {
            title: t('rapoarte.amount'),
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            render: value => formatNumber(value, 0),
        },
        { title: t('rapoarte.notes'), dataIndex: 'notes', key: 'notes', ellipsis: true },
        {
            title: t('rapoarte.difference'),
            dataIndex: 'difference',
            key: 'difference',
            align: 'right',
            render: value => formatNumber(value, 0),
        },
        { title: t('rapoarte.user'), dataIndex: 'user', key: 'user' },
    ];

    const annexColumns: ColumnsType<RapoarteRow> =
        annexKind === 'gold' ? goldColumns : electronicsColumns;

    const columnsByTab: Record<string, ColumnsType<RapoarteRow>> = {
        'gold-contracts': goldColumns,
        'electronics-contracts': electronicsColumns,
        'sold-goods': soldGoodsColumns,
        consignment: consignmentColumns,
        'active-accounting': activeAccountingColumns,
        'cash-handovers': cashHandoverColumns,
    };

    const renderStandardTable = () => (
        <Table
            rowKey={(_, index) => String(index)}
            columns={columnsByTab[activeTab]}
            dataSource={rows}
            loading={loading}
            size="small"
            pagination={{ pageSize: 25, showSizeChanger: true }}
            scroll={{ x: true }}
        />
    );

    const renderConsignmentSummary = () =>
        consignmentSummary ? (
            <div className="kpi-grid" style={{ marginBottom: 16 }}>
                <SummaryStat
                    title={t('rapoarte.consignmentTotal')}
                    value={consignmentSummary.total}
                    precision={2}
                    accent="#b45309"
                    icon={<GoldOutlined />}
                />
                <SummaryStat
                    title="Au 585"
                    value={consignmentSummary.au585}
                    precision={2}
                    accent="#0d9488"
                    icon={<GoldOutlined />}
                />
                <SummaryStat
                    title="Au 750"
                    value={consignmentSummary.au750}
                    precision={2}
                    accent="#4f46e5"
                    icon={<GoldOutlined />}
                />
            </div>
        ) : null;

    const renderActiveSummary = () =>
        activeSummary ? (
            <div className="kpi-grid" style={{ marginBottom: 16 }}>
                <SummaryStat
                    title={t('rapoarte.contractsCount')}
                    value={activeSummary.count}
                    accent="#1677ff"
                    icon={<NumberOutlined />}
                />
                <SummaryStat
                    title={t('rapoarte.contractsTotalLei')}
                    value={activeSummary.totalLei}
                    precision={0}
                    accent="#16a34a"
                    icon={<DollarOutlined />}
                />
            </div>
        ) : null;

    const renderAnnexes = () => (
        <Row gutter={16}>
            <Col xs={24} md={7}>
                <Card size="small" title={t('rapoarte.annexDates')}>
                    <Space style={{ marginBottom: 12 }}>
                        <Button
                            type={annexKind === 'gold' ? 'primary' : 'default'}
                            onClick={() =>
                                selectedAnnexDate && loadAnnex(selectedAnnexDate, 'gold')
                            }
                        >
                            {t('rapoarte.annexGold')}
                        </Button>
                        <Button
                            type={annexKind === 'electronics' ? 'primary' : 'default'}
                            onClick={() =>
                                selectedAnnexDate && loadAnnex(selectedAnnexDate, 'electronics')
                            }
                        >
                            {t('rapoarte.annexElectronics')}
                        </Button>
                        <Button
                            icon={<FileExcelOutlined />}
                            disabled={!selectedAnnexDate}
                            onClick={() => void handleExportAnnex()}
                        >
                            {t('rapoarte.exportExcel')}
                        </Button>
                    </Space>
                    <List
                        size="small"
                        bordered
                        dataSource={annexDates}
                        style={{ maxHeight: 420, overflow: 'auto' }}
                        renderItem={date => (
                            <List.Item
                                onClick={() => loadAnnex(date, annexKind)}
                                style={{
                                    cursor: 'pointer',
                                    background:
                                        date === selectedAnnexDate ? '#e6f4ff' : undefined,
                                }}
                            >
                                {formatDate(date)}
                            </List.Item>
                        )}
                    />
                </Card>
            </Col>
            <Col xs={24} md={17}>
                <Table
                    rowKey={(_, index) => String(index)}
                    columns={annexColumns}
                    dataSource={annexRows}
                    loading={loading}
                    size="small"
                    pagination={{ pageSize: 25 }}
                    scroll={{ x: true }}
                />
            </Col>
        </Row>
    );

    const tabItems = [
        { key: 'gold-contracts', label: t('rapoarte.goldContracts') },
        { key: 'electronics-contracts', label: t('rapoarte.electronicsContracts') },
        { key: 'sold-goods', label: t('rapoarte.soldGoods') },
        { key: 'consignment', label: t('rapoarte.consignment') },
        { key: 'active-accounting', label: t('rapoarte.activeAccounting') },
        { key: 'annexes', label: t('rapoarte.annexes') },
        { key: 'cash-handovers', label: t('rapoarte.cashHandovers') },
    ];

    let tabContent: JSX.Element;
    if (activeTab === 'annexes') {
        tabContent = renderAnnexes();
    } else if (activeTab === 'consignment') {
        tabContent = (
            <>
                {renderConsignmentSummary()}
                {renderStandardTable()}
            </>
        );
    } else if (activeTab === 'active-accounting') {
        tabContent = (
            <>
                {renderActiveSummary()}
                {renderStandardTable()}
            </>
        );
    } else {
        tabContent = renderStandardTable();
    }

    return (
        <div className="rapoarte-page">
            <div
                className="page-header"
                style={{ ['--page-header-accent' as string]: '#4f46e5' } as React.CSSProperties}
            >
                <div className="page-header__lead">
                    <span className="page-header__icon">
                        <ProfileOutlined aria-hidden />
                    </span>
                    <div>
                        <Title level={2} style={{ margin: 0 }}>
                            {t('rapoarte.title')}
                        </Title>
                        <Text type="secondary">{t('rapoarte.subtitle')}</Text>
                    </div>
                </div>
                <div className="page-header__actions">
                    {usesDate && (
                        <DatePicker
                            value={referenceDate}
                            onChange={date => date && setReferenceDate(date)}
                            format="DD/MM/YYYY"
                            allowClear={false}
                        />
                    )}
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={activeTab === 'annexes' ? loadAnnexDates : loadTab}
                    >
                        {t('rapoarte.refresh')}
                    </Button>
                    {supportsExport && (
                        <Button type="primary" icon={<FileExcelOutlined />} onClick={handleExport}>
                            {t('rapoarte.exportExcel')}
                        </Button>
                    )}
                    <Button icon={<PrinterOutlined />} onClick={() => window.print()}>
                        {t('rapoarte.print')}
                    </Button>
                </div>
            </div>

            <Card>
                <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
                {tabContent}
            </Card>
        </div>
    );
};

export default Rapoarte;
